// Hash router (#/path/param). Each screen exports render(el, params) and may return a cleanup function. A screen is
// given as its module, or as a loader (`() => import(...)`): the module then comes as its own chunk the first time
// the screen opens, and the app's first load carries only the home screen.
import { t } from '../i18n/index.js';
import { errorText } from '../i18n/content.js';
import { BUSY_DELAY_MS } from '@gruvetto/browser';
import { esc, closeModals } from './dom.js';
import { legacyRoute } from './legacy-routes.js';
import { isLoggedIn } from '../data/api.js';

/** A screen module. Each declares the route parameters it destructures (`{ id }`, `{ tab }`…): the router passes the match's groups. */
export interface Screen { render(el: HTMLElement, params: any): unknown }
type ScreenSource = Screen | (() => Promise<Screen>);
interface Route { re: RegExp; screen: ScreenSource; nav: string; pattern: string; loaded: Screen | null }
const routes: Route[] = [];
/** Without a session every address shows the welcome screen (ADR 0013); the return from Google or Strava, a password reset link and the legal pages are the exceptions. */
const welcome: Route = { re: /^$/, screen: () => import('./screens/welcome.js'), nav: 'welcome', pattern: '', loaded: null };
const gated = (path: string) => !isLoggedIn() && !path.startsWith('/signin/') && !path.startsWith('/reset/') && !path.startsWith('/legal');
let cleanup: unknown = null, current: Route | null = null;

export function route(pattern: string, screen: ScreenSource, nav: string) { routes.push({ re: new RegExp('^' + pattern.replace(/:(\w+)/g, '(?<$1>[^/]+)') + '$'), screen, nav, pattern, loaded: typeof screen === 'function' ? null : screen }); }
/** The screen's module, loaded once. */
async function screenOf(r: Route): Promise<Screen> {
  if (!r.loaded) r.loaded = typeof r.screen === 'function' ? await r.screen() : r.screen;
  return r.loaded;
}
export const navigate = (path: string) => { if (location.hash !== '#' + path) location.hash = path; else render(); };
export const currentPath = () => location.hash.slice(1) || '/';

export async function render() {
  const path = currentPath();
  const renamed = legacyRoute(path);
  // replace, not assign: the French address must not stay in the history
  if (renamed) { location.replace('#' + renamed); return; }
  const r = gated(path) ? welcome : routes.find(x => x.re.test(path)) || routes[0];
  const params = r.re.exec(path)?.groups || {};
  document.body.classList.toggle('signed-out', !isLoggedIn());
  if (typeof cleanup === 'function') { try { cleanup(); } catch (e) { console.error(e); } }
  cleanup = null;
  closeModals(); // a dialog of the screen left must not float over the next one
  current = r;
  const root = document.getElementById('content')!;
  root.scrollTop = 0;
  root.innerHTML = '';
  // fresh container for each screen: delegated listeners disappear with it
  const el = document.createElement('div');
  root.appendChild(el);
  document.dispatchEvent(new CustomEvent('route', { detail: { path, nav: r.nav } }));
  // a screen still empty after a moment (its chunk or its first data on a slow network) says so rather than look frozen
  const waiting = setTimeout(() => {
    if (!el.childElementCount) el.innerHTML = `<div class="page" data-route-wait><div class="empty inline gap-2" role="status"><span class="spinner"></span>${esc(t.common.loading)}</div></div>`;
  }, BUSY_DELAY_MS);
  try {
    const screen = await screenOf(r);
    if (current !== r || !el.isConnected) return; // the player moved on while the chunk was loading
    const done = await screen.render(el, params);
    // the player moved on while the screen loaded its data: its timers and observers go now, and the screen shown
    // keeps its own cleanup
    if (!el.isConnected) { if (typeof done === 'function') { try { done(); } catch (e) { console.error(e); } } return; }
    cleanup = done;
  } catch (e) {
    console.error(e);
    el.innerHTML = `<div class="page"><div class="card"><h2>${esc(t.common.oops)}</h2><p class="mut">${esc(errorText(String((e as Error).message || e)))}</p></div></div>`;
  } finally {
    clearTimeout(waiting);
    el.querySelector(':scope > [data-route-wait]')?.remove();
  }
}

/** The registered address patterns (`/club/:id`…), for the screen tests. */
export const routePatterns = () => routes.map(r => r.pattern);
export const refresh = () => render();
export const start = () => { addEventListener('hashchange', render); render(); };
export const active = () => current;
